import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { IntlShape, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useDebounce } from 'use-debounce';
import qs from 'qs';
import urljoin from 'url-join';
import startCase from 'lodash/startCase';
import {
  IconCustomer2,
  IconFinance,
  IconMail,
  IconMarketplace,
  IconOrders,
  IconSearch,
  IconSettings,
  IconStats,
  IconWarehouse2,
  FailedOrder,
} from '../../../assets/svg/icons';
import routes from '../../../setup/consts/routes';
import apiRoutes from '../../../setup/consts/apiRoutes';
import SupportedLanguages from '../../../setup/intl/enums';
import { MARKETPLACE_TEST_BOOT } from '../../layouts/MainLayout/consts';
import { GetOrdersResponseSingleItem } from '../../../setup/apiTypes/orders';
import { GetCustomersResponseSingleItem } from '../../../setup/apiTypes/customers';
import { GetWarehouseProductsResponseSingleItem } from '../../../setup/apiTypes/warehouse';
import { GetOrderStatusListResponse } from '../../../pages/orders/types';
import { ListingEndpointResponse } from '../../molecules/Table/types';
import { statusTone } from '../../../pages/orders/consts';
import Badge from '../../atoms/Badge';
import Spinner from '../../atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../atoms/Spinner/enums';
import { formatIqd } from '../../../utils/money';
import { formatIraqPhone } from '../../../utils/phone';
import { cityLabel } from '../../../utils/cities';
import {
  StyledPaletteBackdrop,
  StyledPaletteEmpty,
  StyledPaletteFooter,
  StyledPaletteFooterGroup,
  StyledPaletteGroup,
  StyledPaletteHint,
  StyledPaletteInput,
  StyledPaletteInputRow,
  StyledPaletteItem,
  StyledPaletteItemBody,
  StyledPaletteItemMeta,
  StyledPaletteItemRight,
  StyledPaletteItemTitle,
  StyledPaletteList,
  StyledPaletteNote,
  StyledPalettePanel,
  StyledPaletteValue,
} from './styles';

const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_GROUP = 5;
const SEARCH_DEBOUNCE_MS = 300;

type CommandGroup = 'navigate' | 'create' | 'help';

type Command = {
  id: string;
  group: CommandGroup;
  title: string;
  meta?: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  keywords?: string[];
  run: (history: ReturnType<typeof useHistory>) => void;
};

const buildCommands = (intl: IntlShape): Command[] => [
  {
    id: 'nav-dashboard',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.dashboard',
      defaultMessage: 'Dashboard',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaDashboard',
      defaultMessage: 'Home — what needs attention',
    }),
    icon: IconStats,
    keywords: ['dashboard', 'home', 'overview', 'kpi', 'metrics'],
    run: (h) => h.push(routes.DASHBOARD),
  },
  {
    id: 'nav-warehouse',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.warehouse',
      defaultMessage: 'Warehouse',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaWarehouse',
      defaultMessage: 'Browse and manage products',
    }),
    icon: IconWarehouse2,
    keywords: ['warehouse', 'stock', 'inventory', 'products'],
    run: (h) => h.push(routes.WAREHOUSE),
  },
  {
    id: 'nav-orders',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.orders',
      defaultMessage: 'Orders',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaOrders',
      defaultMessage: 'Track and process orders',
    }),
    icon: IconOrders,
    keywords: ['orders', 'shipments', 'deliveries'],
    run: (h) => h.push(routes.ORDERS),
  },
  {
    id: 'nav-failed-orders',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.failedOrders',
      defaultMessage: 'Failed Orders',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaFailedOrders',
      defaultMessage: 'Orders that need attention',
    }),
    icon: FailedOrder,
    keywords: ['failed orders', 'errors', 'returns', 'issues'],
    run: (h) => h.push(routes.FAILED_ORDERS),
  },
  {
    id: 'nav-financials',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.financials',
      defaultMessage: 'Financials',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaFinancials',
      defaultMessage: 'Revenue, fees and settlements',
    }),
    icon: IconFinance,
    keywords: ['financials', 'money', 'reports', 'revenue', 'invoice'],
    run: (h) => h.push(routes.FINANCIALS),
  },
  {
    id: 'nav-customers',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.customers',
      defaultMessage: 'Customers',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaCustomers',
      defaultMessage: 'View customer history',
    }),
    icon: IconCustomer2,
    keywords: ['customers', 'clients', 'people'],
    run: (h) => h.push(routes.CUSTOMERS),
  },
  {
    id: 'nav-marketplace',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.marketplace',
      defaultMessage: 'Marketplace',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaMarketplace',
      defaultMessage: 'Discover products and brands',
    }),
    icon: IconMarketplace,
    keywords: ['marketplace', 'shop', 'discover', 'browse'],
    run: (h) => h.push(routes.MARKETPLACE),
  },
  {
    id: 'nav-contact',
    group: 'help',
    title: intl.formatMessage({
      id: 'mainNavigation.contactUs',
      defaultMessage: 'Contact Us',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaContact',
      defaultMessage: 'Reach Sandoog support',
    }),
    icon: IconMail,
    keywords: ['contact us', 'support', 'help'],
    run: (h) => h.push(routes.CONTACT_US),
  },
  {
    id: 'nav-settings',
    group: 'navigate',
    title: intl.formatMessage({
      id: 'mainNavigation.settings',
      defaultMessage: 'Settings',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaSettings',
      defaultMessage: 'Account, billing, integrations',
    }),
    icon: IconSettings,
    keywords: ['settings', 'profile', 'config', 'preferences'],
    run: (h) => h.push(routes.ACCOUNT_DETAILS),
  },
  {
    id: 'create-order',
    group: 'create',
    title: intl.formatMessage({
      id: 'orders.createNewOrder',
      defaultMessage: 'New order',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaCreateOrder',
      defaultMessage: 'Start the order entry flow',
    }),
    icon: IconOrders,
    keywords: ['place a new order', 'new', 'order', 'add', 'place'],
    run: (h) => h.push(routes.PLACE_ORDER),
  },
  {
    id: 'create-product',
    group: 'create',
    title: intl.formatMessage({
      id: 'warehouse.addProduct',
      defaultMessage: 'Add product',
    }),
    meta: intl.formatMessage({
      id: 'palette.metaCreateProduct',
      defaultMessage: 'Create a new warehouse item',
    }),
    icon: IconWarehouse2,
    keywords: ['add a product', 'new', 'product', 'sku', 'add'],
    run: (h) => h.push(`${routes.WAREHOUSE}/product`),
  },
];

const score = (cmd: Command, query: string) => {
  if (!query) return 1;
  const q = query.toLowerCase();
  const haystack = [cmd.title, cmd.meta ?? '', ...(cmd.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  if (cmd.title.toLowerCase().startsWith(q)) return 100;
  if (haystack.includes(q)) return 10;
  // simple subsequence
  let qi = 0;
  for (let i = 0; i < haystack.length && qi < q.length; i += 1) {
    if (haystack[i] === q[qi]) qi += 1;
  }
  return qi === q.length ? 1 : 0;
};

type Row =
  | { kind: 'command'; id: string; cmd: Command }
  | { kind: 'order'; id: string; order: GetOrdersResponseSingleItem }
  | { kind: 'customer'; id: string; customer: GetCustomersResponseSingleItem }
  | { kind: 'product'; id: string; product: GetWarehouseProductsResponseSingleItem };

const ENTITY_QUERY_OPTIONS = {
  retry: 1,
  staleTime: 60_000,
  keepPreviousData: true,
  refetchOnMount: false,
} as const;

const CommandPaletteInner: React.FC = () => {
  const history = useHistory();
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global hotkey: ⌘K / Ctrl+K toggles. Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isOpenCombo =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isOpenCombo) {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      // delay one tick so the input is mounted
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const commands = useMemo(() => buildCommands(intl), [intl]);

  const trimmed = query.trim();
  const searchActive = trimmed.length >= MIN_QUERY_LENGTH;
  const [debounced] = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);
  const term = debounced.trim();
  const termReady = open && term.length >= MIN_QUERY_LENGTH && searchActive;
  // true while the user is still typing and the debounced term lags behind
  const debouncePending = searchActive && term !== trimmed;

  const ordersQuery = useQuery<
    UseQueryResponse<ListingEndpointResponse<GetOrdersResponseSingleItem>>
  >(
    urljoin(
      apiRoutes.ORDERS,
      qs.stringify(
        { search: term, page: 1, pageSize: RESULTS_PER_GROUP },
        { addQueryPrefix: true }
      )
    ),
    { ...ENTITY_QUERY_OPTIONS, enabled: termReady }
  );

  const customersQuery = useQuery<
    UseQueryResponse<ListingEndpointResponse<GetCustomersResponseSingleItem>>
  >(
    urljoin(
      apiRoutes.CUSTOMERS,
      qs.stringify(
        { query: term, pageIndex: 1, pageSize: RESULTS_PER_GROUP },
        { addQueryPrefix: true }
      )
    ),
    { ...ENTITY_QUERY_OPTIONS, enabled: termReady }
  );

  const productsQuery = useQuery<
    UseQueryResponse<ListingEndpointResponse<GetWarehouseProductsResponseSingleItem>>
  >(
    urljoin(
      apiRoutes.PRODUCTS,
      qs.stringify(
        {
          query: term,
          pageIndex: 1,
          pageSize: RESULTS_PER_GROUP,
          ascending: true,
        },
        { addQueryPrefix: true }
      )
    ),
    { ...ENTITY_QUERY_OPTIONS, enabled: termReady }
  );

  // Localized status labels — same source the orders table uses. Fetched only
  // once a search actually runs, then cached for the session.
  const statusListQuery = useQuery<
    UseQueryResponse<GetOrderStatusListResponse>
  >(apiRoutes.ORDER_STATUS_LIST, {
    enabled: termReady,
    retry: 1,
    staleTime: Infinity,
    refetchOnMount: false,
  });

  const statusLabels = useMemo(() => {
    const map = new Map<string, string>();
    const isEnglish = intl.locale === SupportedLanguages.English;
    statusListQuery.data?.response?.statuses?.forEach(
      ({ status, enStatusName, arStatusName }) => {
        map.set(status, isEnglish ? startCase(enStatusName) : arStatusName);
      }
    );
    return map;
  }, [statusListQuery.data, intl.locale]);

  const navRanked = useMemo(
    () =>
      commands
        .map((cmd) => ({ cmd, s: score(cmd, trimmed) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.cmd),
    [commands, trimmed]
  );

  const orderItems = useMemo(
    () => (termReady ? ordersQuery.data?.response?.items ?? [] : []),
    [termReady, ordersQuery.data]
  );
  const customerItems = useMemo(
    () => (termReady ? customersQuery.data?.response?.items ?? [] : []),
    [termReady, customersQuery.data]
  );
  const productItems = useMemo(
    () => (termReady ? productsQuery.data?.response?.items ?? [] : []),
    [termReady, productsQuery.data]
  );

  const rows: Row[] = useMemo(() => {
    if (!searchActive) {
      return navRanked.map((cmd) => ({ kind: 'command', id: cmd.id, cmd }));
    }

    return [
      ...orderItems.map<Row>((order) => ({
        kind: 'order',
        id: `order-${order.id}`,
        order,
      })),
      ...customerItems.map<Row>((customer) => ({
        kind: 'customer',
        id: `customer-${customer.id}`,
        customer,
      })),
      ...productItems.map<Row>((product) => ({
        kind: 'product',
        id: `product-${product.productId}`,
        product,
      })),
      ...navRanked.map<Row>((cmd) => ({ kind: 'command', id: cmd.id, cmd })),
    ];
  }, [searchActive, navRanked, orderItems, customerItems, productItems]);

  const activeIdx = Math.min(selectedIdx, Math.max(rows.length - 1, 0));

  const close = useCallback(() => setOpen(false), []);

  const runRow = useCallback(
    (row: Row | undefined) => {
      if (!row) return;
      close();
      if (row.kind === 'command') {
        row.cmd.run(history);
      } else if (row.kind === 'order') {
        history.push(routes.ORDER_DETAIL.replace(':orderId', row.order.id));
      } else if (row.kind === 'customer') {
        history.push({
          pathname: routes.CUSTOMERS,
          search: qs.stringify(
            { customerId: row.customer.id },
            { addQueryPrefix: true }
          ),
        });
      } else {
        history.push(
          generatePath(routes.SHOW_PRODUCT, { id: row.product.productId })
        );
      }
    },
    [history, close]
  );

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(Math.min(activeIdx + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(Math.max(activeIdx - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runRow(rows[activeIdx]);
    }
  };

  if (!open) return null;

  const groupLabels: Record<CommandGroup, string> = {
    navigate: intl.formatMessage({
      id: 'palette.groupNavigate',
      defaultMessage: 'Navigate',
    }),
    create: intl.formatMessage({
      id: 'palette.groupCreate',
      defaultMessage: 'Create',
    }),
    help: intl.formatMessage({
      id: 'palette.groupHelp',
      defaultMessage: 'Help',
    }),
  };

  const renderCommandItem = (cmd: Command, flatIdx: number) => {
    const Icon = cmd.icon;
    return (
      <StyledPaletteItem
        key={cmd.id}
        active={flatIdx === activeIdx}
        onMouseEnter={() => setSelectedIdx(flatIdx)}
        onClick={() => runRow(rows[flatIdx])}
      >
        <Icon />
        <StyledPaletteItemBody>
          <StyledPaletteItemTitle>{cmd.title}</StyledPaletteItemTitle>
          {cmd.meta ? (
            <StyledPaletteItemMeta>{cmd.meta}</StyledPaletteItemMeta>
          ) : null}
        </StyledPaletteItemBody>
      </StyledPaletteItem>
    );
  };

  let listContent: React.ReactNode;

  if (!searchActive) {
    // Empty (or sub-2-char) query: nav-first, original grouped behavior.
    const grouped = new Map<CommandGroup, Command[]>();
    navRanked.forEach((cmd) => {
      const arr = grouped.get(cmd.group) ?? [];
      arr.push(cmd);
      grouped.set(cmd.group, arr);
    });

    listContent =
      navRanked.length === 0 ? (
        <StyledPaletteEmpty>
          {intl.formatMessage(
            {
              id: 'palette.noResults',
              defaultMessage: 'No results for “{q}”',
            },
            { q: trimmed }
          )}
        </StyledPaletteEmpty>
      ) : (
        Array.from(grouped.entries()).map(([group, items]) => (
          <React.Fragment key={group}>
            <StyledPaletteGroup>{groupLabels[group]}</StyledPaletteGroup>
            {items.map((cmd) =>
              renderCommandItem(
                cmd,
                rows.findIndex((r) => r.id === cmd.id)
              )
            )}
          </React.Fragment>
        ))
      );
  } else {
    const ordersBase = 0;
    const customersBase = orderItems.length;
    const productsBase = customersBase + customerItems.length;
    const navBase = productsBase + productItems.length;

    const sections: {
      key: string;
      label: string;
      loading: boolean;
      error: boolean;
      errorLabel: string;
      count: number;
      total?: number;
      render: () => React.ReactNode;
    }[] = [
      {
        key: 'orders',
        label: intl.formatMessage({
          id: 'palette.groupOrders',
          defaultMessage: 'Orders',
        }),
        loading: debouncePending || ordersQuery.isFetching,
        error: ordersQuery.isError,
        errorLabel: intl.formatMessage({
          id: 'palette.errorOrders',
          defaultMessage: "Couldn't search orders",
        }),
        count: orderItems.length,
        total: termReady ? ordersQuery.data?.response?.total : undefined,
        render: () =>
          orderItems.map((order, i) => {
            const flatIdx = ordersBase + i;
            return (
              <StyledPaletteItem
                key={`order-${order.id}`}
                active={flatIdx === activeIdx}
                onMouseEnter={() => setSelectedIdx(flatIdx)}
                onClick={() => runRow(rows[flatIdx])}
              >
                <IconOrders />
                <StyledPaletteItemBody>
                  <StyledPaletteItemTitle dir='auto'>
                    {order.receiptNumber || order.id}
                  </StyledPaletteItemTitle>
                  <StyledPaletteItemMeta dir='auto'>
                    {order.name || '—'}
                  </StyledPaletteItemMeta>
                </StyledPaletteItemBody>
                <StyledPaletteItemRight>
                  <Badge tone={statusTone(order.status)} dot>
                    {statusLabels.get(order.status) ?? startCase(order.status)}
                  </Badge>
                  <StyledPaletteValue>
                    {formatIqd(order.value)} IQD
                  </StyledPaletteValue>
                </StyledPaletteItemRight>
              </StyledPaletteItem>
            );
          }),
      },
      {
        key: 'customers',
        label: intl.formatMessage({
          id: 'palette.groupCustomers',
          defaultMessage: 'Customers',
        }),
        loading: debouncePending || customersQuery.isFetching,
        error: customersQuery.isError,
        errorLabel: intl.formatMessage({
          id: 'palette.errorCustomers',
          defaultMessage: "Couldn't search customers",
        }),
        count: customerItems.length,
        total: termReady ? customersQuery.data?.response?.total : undefined,
        render: () =>
          customerItems.map((customer, i) => {
            const flatIdx = customersBase + i;
            const city = cityLabel(
              customer.city || customer.addresses?.[0]?.state,
              intl.locale as 'en' | 'ar'
            );
            return (
              <StyledPaletteItem
                key={`customer-${customer.id}`}
                active={flatIdx === activeIdx}
                onMouseEnter={() => setSelectedIdx(flatIdx)}
                onClick={() => runRow(rows[flatIdx])}
              >
                <IconCustomer2 />
                <StyledPaletteItemBody>
                  <StyledPaletteItemTitle dir='auto'>
                    {customer.name || '—'}
                  </StyledPaletteItemTitle>
                  <StyledPaletteItemMeta dir='auto'>
                    {city}
                    {city && customer.phone ? ' · ' : ''}
                    {customer.phone ? (
                      <span
                        dir='ltr'
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontVariantNumeric: 'tabular-nums',
                          unicodeBidi: 'embed',
                        }}
                      >
                        {formatIraqPhone(customer.phone)}
                      </span>
                    ) : null}
                  </StyledPaletteItemMeta>
                </StyledPaletteItemBody>
              </StyledPaletteItem>
            );
          }),
      },
      {
        key: 'products',
        label: intl.formatMessage({
          id: 'palette.groupProducts',
          defaultMessage: 'Products',
        }),
        loading: debouncePending || productsQuery.isFetching,
        error: productsQuery.isError,
        errorLabel: intl.formatMessage({
          id: 'palette.errorProducts',
          defaultMessage: "Couldn't search products",
        }),
        count: productItems.length,
        total: termReady ? productsQuery.data?.response?.total : undefined,
        render: () =>
          productItems.map((product, i) => {
            const flatIdx = productsBase + i;
            return (
              <StyledPaletteItem
                key={`product-${product.productId}`}
                active={flatIdx === activeIdx}
                onMouseEnter={() => setSelectedIdx(flatIdx)}
                onClick={() => runRow(rows[flatIdx])}
              >
                <IconWarehouse2 />
                <StyledPaletteItemBody>
                  <StyledPaletteItemTitle dir='auto'>
                    {product.productName || '—'}
                  </StyledPaletteItemTitle>
                  <StyledPaletteItemMeta dir='auto'>
                    {product.sku ? (
                      <span dir='ltr' style={{ unicodeBidi: 'embed' }}>
                        {product.sku}
                      </span>
                    ) : null}
                    {product.sku ? ' · ' : ''}
                    {intl.formatMessage(
                      {
                        id: 'palette.inStock',
                        defaultMessage: '{count} in stock',
                      },
                      { count: product.onHandQuantity ?? 0 }
                    )}
                  </StyledPaletteItemMeta>
                </StyledPaletteItemBody>
              </StyledPaletteItem>
            );
          }),
      },
    ];

    const anyLoading = sections.some((s) => s.loading);
    const anyError = sections.some((s) => s.error);
    const anyResults = sections.some((s) => s.count > 0);
    const showGlobalEmpty =
      !anyLoading && !anyError && !anyResults && navRanked.length === 0;

    listContent = showGlobalEmpty ? (
      <StyledPaletteEmpty>
        {intl.formatMessage(
          { id: 'palette.noResults', defaultMessage: 'No results for “{q}”' },
          { q: trimmed }
        )}
      </StyledPaletteEmpty>
    ) : (
      <>
        {sections.map((section) => {
          if (!section.loading && !section.error && section.count === 0) {
            return null;
          }
          const remaining =
            section.total !== undefined && section.total > section.count
              ? section.total - section.count
              : 0;
          return (
            <React.Fragment key={section.key}>
              <StyledPaletteGroup>
                {section.label}
                {section.loading ? (
                  <Spinner
                    mode={SpinnerMode.Dark}
                    size={SpinnerSize.ExtraSmall}
                  />
                ) : null}
              </StyledPaletteGroup>
              {section.error ? (
                <StyledPaletteNote tone='danger'>
                  {section.errorLabel}
                </StyledPaletteNote>
              ) : (
                <>
                  {section.render()}
                  {remaining > 0 ? (
                    <StyledPaletteNote>
                      {intl.formatMessage(
                        { id: 'palette.more', defaultMessage: '{count} more…' },
                        { count: remaining }
                      )}
                    </StyledPaletteNote>
                  ) : null}
                </>
              )}
            </React.Fragment>
          );
        })}
        {navRanked.length > 0 ? (
          <>
            <StyledPaletteGroup>{groupLabels.navigate}</StyledPaletteGroup>
            {navRanked.map((cmd, i) => renderCommandItem(cmd, navBase + i))}
          </>
        ) : null}
      </>
    );
  }

  const palette = (
    <StyledPaletteBackdrop
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <StyledPalettePanel role='dialog' aria-label='Command palette'>
        <StyledPaletteInputRow>
          <IconSearch />
          <StyledPaletteInput
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIdx(0);
            }}
            onKeyDown={onInputKey}
            placeholder={intl.formatMessage({
              id: 'palette.placeholder',
              defaultMessage: 'Search orders, customers, products…',
            })}
            autoComplete='off'
            spellCheck={false}
          />
          <StyledPaletteHint>ESC</StyledPaletteHint>
        </StyledPaletteInputRow>

        <StyledPaletteList>{listContent}</StyledPaletteList>

        <StyledPaletteFooter>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>↑</StyledPaletteHint>
            <StyledPaletteHint>↓</StyledPaletteHint>
            <span>
              {intl.formatMessage({
                id: 'palette.footerNavigate',
                defaultMessage: 'navigate',
              })}
            </span>
          </StyledPaletteFooterGroup>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>↵</StyledPaletteHint>
            <span>
              {intl.formatMessage({
                id: 'palette.footerOpen',
                defaultMessage: 'open',
              })}
            </span>
          </StyledPaletteFooterGroup>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>⌘K</StyledPaletteHint>
            <span>
              {intl.formatMessage({
                id: 'palette.footerToggle',
                defaultMessage: 'toggle',
              })}
            </span>
          </StyledPaletteFooterGroup>
        </StyledPaletteFooter>
      </StyledPalettePanel>
    </StyledPaletteBackdrop>
  );

  return ReactDOM.createPortal(palette, document.body);
};

const CommandPalette: React.FC = () => {
  if (MARKETPLACE_TEST_BOOT) return null;
  return <CommandPaletteInner />;
};

export default CommandPalette;
